import React from 'react';
import {
  Toaster as SonnerToaster,
  ToasterProps as SonnerToasterProps,
} from 'sonner';
import { cn } from '../../utils/cn';

export interface ToasterProps extends Omit<SonnerToasterProps, 'position'> {
  /**
   * Position of the toaster
   * @default 'bottom-right'
   */
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
  /**
   * Custom className for the toaster container
   */
  className?: string;
  /**
   * Theme for the toaster
   * @default 'light'
   */
  theme?: 'light' | 'dark' | 'system';
  /**
   * Duration in milliseconds before the toast auto-dismisses
   * @default 3000
   */
  duration?: number;
}

/**
 * Flexible Toaster component built on top of Sonner
 * Supports all positions and can be customized with Tailwind classes
 */
export const Toaster: React.FC<ToasterProps> = ({
  position = 'bottom-right',
  className,
  theme = 'light',
  richColors = true,
  closeButton = false,
  duration = 3000,
  ...props
}) => {
  return (
    <SonnerToaster
      position={position}
      theme={theme}
      richColors={richColors}
      closeButton={closeButton}
      duration={duration}
      className={cn('toaster group', className)}
      style={
        {
          zIndex: 1200,
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: cn(
            'group toast group-[.toaster]:bg-white group-[.toaster]:text-gray-900',
            'group-[.toaster]:border-gray-200 group-[.toaster]:shadow-lg',
            // Increased padding, width and base font size for larger, more prominent toasts
            'group-[.toaster]:rounded-lg group-[.toaster]:p-4',
            'group-[.toaster]:w-80 group-[.toaster]:xl:w-96',
            'group-[.toaster]:text-base xl:group-[.toaster]:text-lg',
            'dark:group-[.toaster]:bg-gray-800 dark:group-[.toaster]:text-gray-100',
            'dark:group-[.toaster]:border-gray-700'
          ),
          description: cn(
            'group-[.toast]:text-gray-600 dark:group-[.toast]:text-gray-400',
            // Make the description text slightly larger for readability
            'group-[.toast]:text-base xl:group-[.toast]:text-lg'
          ),
          actionButton: cn(
            'group-[.toast]:bg-primary-600 group-[.toast]:text-white',
            'group-[.toast]:hover:bg-primary-700 group-[.toast]:rounded-md',
            'group-[.toast]:px-3 group-[.toast]:py-2 group-[.toast]:text-sm',
            'group-[.toast]:font-medium group-[.toast]:transition-colors'
          ),
          cancelButton: cn(
            'group-[.toast]:bg-gray-200 group-[.toast]:text-gray-900',
            'group-[.toast]:hover:bg-gray-300 group-[.toast]:rounded-md',
            'group-[.toast]:px-3 group-[.toast]:py-2 group-[.toast]:text-sm',
            'group-[.toast]:font-medium group-[.toast]:transition-colors',
            'dark:group-[.toast]:bg-gray-600 dark:group-[.toast]:text-gray-100',
            'dark:group-[.toast]:hover:bg-gray-500'
          ),
          closeButton: cn(
            'group-[.toast]:text-gray-400 group-[.toast]:hover:text-gray-600',
            'dark:group-[.toast]:text-gray-500 dark:group-[.toast]:hover:text-gray-300'
          ),
          success: cn(
            'group-[.toaster]:bg-green-50 group-[.toaster]:text-green-900',
            'group-[.toaster]:border-green-200',
            'dark:group-[.toaster]:bg-green-900/20 dark:group-[.toaster]:text-green-100',
            'dark:group-[.toaster]:border-green-800'
          ),
          error: cn(
            'group-[.toaster]:bg-red-50 group-[.toaster]:text-red-900',
            'group-[.toaster]:border-red-200',
            'dark:group-[.toaster]:bg-red-900/20 dark:group-[.toaster]:text-red-100',
            'dark:group-[.toaster]:border-red-800',
            '!text-base font-semibold' // Increase font size and weight for error toasts
          ),
          warning: cn(
            'group-[.toaster]:bg-yellow-50 group-[.toaster]:text-yellow-900',
            'group-[.toaster]:border-yellow-200',
            'dark:group-[.toaster]:bg-yellow-900/20 dark:group-[.toaster]:text-yellow-100',
            'dark:group-[.toaster]:border-yellow-800'
          ),
          info: cn(
            'group-[.toaster]:bg-blue-50 group-[.toaster]:text-blue-900',
            'group-[.toaster]:border-blue-200',
            'dark:group-[.toaster]:bg-blue-900/20 dark:group-[.toaster]:text-blue-100',
            'dark:group-[.toaster]:border-blue-800'
          ),
        },
        ...props.toastOptions,
      }}
      {...props}
    />
  );
};

export default Toaster;
